import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import loteInformesService from '../../services/loteInformesService';
import type { LoteSaldoDto } from '../../types/lotes-informes';
import { getApiErrorMessage } from '../../utils/apiError';

const LotesVencimientosInforme: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [items, setItems] = useState<LoteSaldoDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [dias, setDias] = useState(30);
  const [resumen, setResumen] = useState<{ vencidos: number; proximos: number } | null>(null);

  const load = async () => {
    try {
      const data = await loteInformesService.vencimientos({
        dias,
        page: page + 1,
        pageSize,
      });
      setItems(data.items);
      setTotal(data.total);
      setResumen(data.resumen ?? null);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al consultar vencimientos'), { variant: 'error' });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
            Lotes próximos a vencer / vencidos
          </Typography>
          {resumen && (
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip color="error" label={`Vencidos: ${resumen.vencidos}`} />
              <Chip color="warning" label={`Próximos: ${resumen.proximos}`} />
            </Stack>
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              size="small"
              type="number"
              label="Días"
              value={dias}
              onChange={(e) => setDias(Number(e.target.value) || 30)}
              sx={{ width: 120 }}
            />
            <Button variant="contained" startIcon={<SearchIcon />} onClick={() => { setPage(0); load(); }}>
              Buscar
            </Button>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Artículo</TableCell>
                  <TableCell>Lote</TableCell>
                  <TableCell>Depósito</TableCell>
                  <TableCell>Vencimiento</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((i) => (
                  <TableRow key={`${i.idLote}-${i.nroUbicacion}`} hover>
                    <TableCell>{i.codigoArticulo}</TableCell>
                    <TableCell>{i.nroLote}</TableCell>
                    <TableCell>{i.nroUbicacion}</TableCell>
                    <TableCell>
                      {i.fechaVencimiento ? String(i.fechaVencimiento).slice(0, 10) : '—'}
                      {i.vencido ? (
                        <Chip size="small" color="error" label="Vencido" sx={{ ml: 1 }} />
                      ) : (
                        <Chip size="small" color="warning" label="Próximo" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell align="right">{i.cantidad}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => navigate(`/inventario/lotes/${i.idLote}`)}>
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default LotesVencimientosInforme;
